import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {WidgetValidator} from '@pt/pt-models';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ValidatorGenerator} from '../../utils/validator-generator';

@Component({
  selector: 'pt-input-select-version',
  templateUrl: './input-select-version.component.html',
  styleUrls: ['./input-select-version.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputSelectVersionComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputSelectVersionComponent),
      multi: true
    }
  ]
})
export class InputSelectVersionComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() placeholder: string;
  @Input() defaultLabel: string;
  @Input() options: InputSelectVersionData[];

  public formGroup: FormGroup;
  isRequired: boolean;
  inputId: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.inputId = 'ptWSelectVersion_' + this.controlName;

    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);

    this.setFormGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.setFormGroup();
    }
  }

  writeValue(obj: any): void {
    this.formGroup.controls.value.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.controls.value.disable();
    } else {
      this.formGroup.controls.value.enable();
    }
  }

  validate(value: FormControl) {
    return this.formGroup.invalid && {
      invalid: true
    };
  }

  private setFormGroup() {
    this.destroy$.next(true);

    const validator = ValidatorGenerator.generateValidator(this.validators);

    let currentVersion = null;
    const selected = this.options?.find((o) => o.selected);
    if (selected) {
      currentVersion = selected.value;
    }
    this.formGroup = new FormGroup({
      value: new FormControl(currentVersion, validator)
    });

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      if (this.onChanged) {
        this.onChanged(t.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });
  }
}

export interface InputSelectVersionData {
  label: Observable<string>,
  selected: boolean,
  value: number
}
